import { useTranslation } from 'react-i18next';

import { Checkbox, Select } from '@/components';

import { ColorPickerAdvanced } from '../controls/ColorPickerAdvanced';
import { NumberInput } from '../controls/NumberInput';
import { RangeSlider } from '../controls/RangeSlider';
import { ShadowEditor } from '../controls/ShadowEditor';
import { BLEND_MODES } from '../styleConstants';
import type { LayoutElementStyle } from '../styleTypes';

interface EffectsSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

export function EffectsSection({ style, onChange }: EffectsSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  const textShadow = style.textShadow || { x: 0, y: 2, blur: 4, color: 'rgba(0,0,0,0.15)' };

  return (
    <div className="space-y-4">
      {/* Box Shadows */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-2">
          {t(`${p}effects.shadows`)}
        </label>
        <ShadowEditor shadows={style.boxShadows} onChange={(s) => onChange({ boxShadows: s })} />
      </div>

      {/* Text Shadow */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider">
            {t(`${p}effects.textShadow`)}
          </label>
          <Checkbox
            checked={!!style.textShadow}
            onChange={(e) => onChange({ textShadow: e.target.checked ? textShadow : undefined })}
            label={<span className="text-[10px] text-gray-400">{t(`${p}common.enable`)}</span>}
            className="h-3 w-3"
          />
        </div>
        {style.textShadow && (
          <div className="p-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2">
            <div className="flex flex-wrap gap-2">
              <NumberInput
                value={style.textShadow.x}
                onChange={(v) => onChange({ textShadow: { ...style.textShadow!, x: v ?? 0 } })}
                min={-20}
                max={20}
                label="X"
                unit="px"
                className="min-w-[6rem] flex-1"
              />
              <NumberInput
                value={style.textShadow.y}
                onChange={(v) => onChange({ textShadow: { ...style.textShadow!, y: v ?? 0 } })}
                min={-20}
                max={20}
                label="Y"
                unit="px"
                className="min-w-[6rem] flex-1"
              />
              <NumberInput
                value={style.textShadow.blur}
                onChange={(v) => onChange({ textShadow: { ...style.textShadow!, blur: v ?? 0 } })}
                min={0}
                max={50}
                label={t(`${p}shadow.blur`)}
                unit="px"
                className="min-w-[6rem] flex-1"
              />
            </div>
            <ColorPickerAdvanced
              color={style.textShadow.color}
              onChange={(c) =>
                onChange({ textShadow: { ...style.textShadow!, color: c || 'rgba(0,0,0,0.15)' } })
              }
              label={t(`${p}common.color`)}
              allowClear={false}
            />
          </div>
        )}
      </div>

      {/* Opacity */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}common.opacity`)}
        </label>
        <div className="flex items-center gap-2">
          <RangeSlider
            min={0}
            max={100}
            value={style.opacity ?? 100}
            onChange={(v) => onChange({ opacity: Math.round(v) })}
            className="flex-1"
            aria-label={t(`${p}common.opacity`)}
          />
          <span className="text-xs text-gray-500 w-10 text-right">{style.opacity ?? 100}%</span>
        </div>
      </div>

      {/* Backdrop Blur */}
      <NumberInput
        value={style.backdropBlur}
        onChange={(v) => onChange({ backdropBlur: v })}
        min={0}
        max={20}
        label={t(`${p}effects.backdropBlur`)}
        unit="px"
        showSlider
      />

      {/* Blend Mode */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}effects.blendMode`)}
        </label>
        <Select
          value={style.mixBlendMode || 'normal'}
          onChange={(e) =>
            onChange({ mixBlendMode: e.target.value as LayoutElementStyle['mixBlendMode'] })
          }
          className="px-2 py-1.5 text-xs"
        >
          {BLEND_MODES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
